import { memo } from 'react'
import { Avatar, styled, Typography } from '@mui/material'
import { AvatarIcon } from '@weblite/icons'
import MuiIconButton from '@mui/material/IconButton'
import { useDisplayedConnectionStatus } from '$comp/main/react-hook'
import { openRoomListSearch, switchToMyAccount } from '$logic/router'
import { CHAT_LIST_APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import { JaryanLogoIcon, SearchIcon } from '$common/icons'
import {
  StoryRibbonsApi,
  useIsStoryRibbonOpen,
} from '$comp/_app/components/story/story-ribbons-api'
import { StoryToggler } from '$comp/_app/components/story/story-toggler'
import { useServerStoryRibbonsWithSeparatedClientUserStory } from '$logic/story/react-hooks'
import { t } from '$i18n'
import { toLocale } from '$helper/functions/text'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { useClientUser } from '$logic/client-user'
import { UserAvatar } from '$common/avatar'
import {
  IS_STORY_ENABLED,
  SHOULD_CATEGORIZE_CHATS,
} from '$func/environmentInfo'

export const ChatAppBar = memo(() => {
  const connectionStatus = useDisplayedConnectionStatus()
  const { id: clientUserId } = useClientUser()

  const { open: isStoryRibbonOpen } = useIsStoryRibbonOpen()

  const {
    nonClientRibbonStories: storyRibbons,
  } = useServerStoryRibbonsWithSeparatedClientUserStory()

  const storyCount = storyRibbons.length

  const onSearchIcon = () => {
    openRoomListSearch()
    analite('CHATLIST_SEARCH_CLICK')
  }

  return (
    <ChatAppBarRoot>
      <IconButton onClick={onSearchIcon}>
        <SearchIcon sx={{ color: 'neutral900' }} />
      </IconButton>
      <ConnectionContainer>
        {connectionStatus ? (
          <Typography
            color="neutral900"
            variant={isStoryRibbonOpen ? 'h3' : 'subtitle2'}
          >
            {connectionStatus}
          </Typography>
        ) : isStoryRibbonOpen || !IS_STORY_ENABLED ? (
          <JaryanLogoIcon sx={{ width: '48px', height: '28px' }} />
        ) : (
          ''
        )}

        {!isStoryRibbonOpen && IS_STORY_ENABLED && (
          <StoryTogglerContainer>
            {!connectionStatus && (
              <Typography
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
                }
                color="neutral1000"
                variant="subtitle2"
                noWrap
              >
                {storyCount > 0
                  ? t('story.nStories')(toLocale(storyCount))
                  : t('story.addStory')}
              </Typography>
            )}

            <StoryToggler
              sx={{ flexShrink: 0, height: '100%' }}
              size={AvatarSize._2XS}
              onClick={() => {
                StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
              }}
            />
          </StoryTogglerContainer>
        )}
      </ConnectionContainer>

      <IconButton onClick={switchToMyAccount}>
        {IS_STORY_ENABLED ? (
          <Avatar component="div" sx={{ backgroundColor: 'transparent' }}>
            <AvatarIcon
              sx={{ width: '24px', height: '24px', color: 'neutral800' }}
            />
          </Avatar>
        ) : (
          <UserAvatar userId={clientUserId} size={AvatarSize._3XS} />
        )}
      </IconButton>
    </ChatAppBarRoot>
  )
})

export const ChatAppBarRoot = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 8px;
  box-sizing: border-box;
  height: ${CHAT_LIST_APP_BAR_HEIGHT}px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  border-bottom: ${({ theme }) =>
    SHOULD_CATEGORIZE_CHATS ? 'none' : `1px solid ${theme.palette.neutral200}`};
`

const ConnectionContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.palette.neutral000};
`

const IconButton = styled(MuiIconButton)`
  color: ${({ theme }) => theme.palette.neutral000};
`

const StoryTogglerContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
`
